import { X } from "lucide-react";

export function PageHead({ eyebrow, title, desc, action, testid }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-testid={testid}>
      <div>
        <p className="font-body text-[12px] font-semibold uppercase tracking-[0.14em] text-[#C1440E]">{eyebrow}</p>
        <h1 className="mt-1 font-heading text-3xl font-semibold text-[#2C241B] md:text-[38px]">{title}</h1>
        {desc && <p className="mt-2 max-w-2xl font-body text-sm text-[#8A8072]">{desc}</p>}
      </div>
      {action}
    </div>
  );
}

export function Stat({ icon: Icon, tint = { bg: "#FBE4D8", fg: "#D84315" }, label, value, sub }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-[#EAE1D3] bg-white p-5 shadow-[0_1px_2px_rgba(44,36,27,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(44,36,27,0.35)]" data-testid={`stat-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-40 blur-2xl transition-opacity group-hover:opacity-70" style={{ background: tint.bg }} />
      <div className="relative flex items-center gap-2.5">
        {Icon && <span className="grid h-10 w-10 place-items-center rounded-xl shadow-inner" style={{ background: tint.bg }}><Icon size={18} style={{ color: tint.fg }} /></span>}
        <span className="font-body text-[13px] font-medium text-[#8A8072]">{label}</span>
      </div>
      <p className="relative mt-4 font-heading text-[32px] leading-none font-semibold text-[#2C241B]">{value}</p>
      {sub && <p className="relative mt-1.5 font-body text-xs text-[#A79C8A]">{sub}</p>}
    </div>
  );
}

export function Modal({ title, onClose, children, footer, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl font-semibold text-[#2C241B]">{title}</h3>
          <button onClick={onClose} className="rounded-md p-1.5 text-[#9A9082] hover:bg-[#F5F1E8]"><X size={18} /></button>
        </div>
        <div className="mt-5">{children}</div>
        {footer && <div className="mt-6 flex justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="font-body text-sm font-medium text-[#2C241B]">{label}</label>
      {children}
    </div>
  );
}

export const inputCls = "mt-1 h-10 w-full rounded-lg border border-[#E5DFD3] px-3 font-body text-sm outline-none focus:border-[#E0A98F]";
export const areaCls = "mt-1 w-full rounded-lg border border-[#E5DFD3] px-3 py-2 font-body text-sm outline-none focus:border-[#E0A98F]";

export function BtnPrimary(props) {
  return <button {...props} className={`rounded-lg bg-gradient-to-r from-[#D84315] to-[#E86A3A] px-4 py-2 font-body text-sm font-semibold text-white shadow-[0_8px_20px_-8px_rgba(216,67,21,0.8)] transition-all hover:shadow-[0_10px_26px_-8px_rgba(216,67,21,0.95)] active:scale-[0.98] disabled:opacity-60 ${props.className || ""}`} />;
}
export function BtnGhost(props) {
  return <button {...props} className={`rounded-lg border border-[#E5DFD3] px-4 py-2 font-body text-sm font-medium text-[#5C5449] hover:bg-[#F5F1E8] ${props.className || ""}`} />;
}

const PILLS = {
  published: "bg-[#E6F0EE] text-[#2F7A6B]", ranking: "bg-[#E6F0EE] text-[#2F7A6B]",
  active: "bg-[#E6F0EE] text-[#2F7A6B]", settled: "bg-[#E6F0EE] text-[#2F7A6B]",
  booked: "bg-[#E6F0EE] text-[#2F7A6B]", scheduled: "bg-[#EEF2E6] text-[#6D8B44]",
  tracking: "bg-[#EEF2E6] text-[#6D8B44]", draft: "bg-[#FBF3E7] text-[#B98A2E]",
  idea: "bg-[#FBF3E7] text-[#B98A2E]", pending: "bg-[#FBF3E7] text-[#B98A2E]",
  open: "bg-[#FBF3E7] text-[#B98A2E]", suspended: "bg-[#FDECEA] text-[#C62828]",
};
export function Pill({ status }) {
  const cls = PILLS[status] || "bg-[#F3EEEA] text-[#8A8072]";
  return <span className={`rounded-full px-2 py-0.5 font-body text-[11px] font-semibold capitalize ${cls}`}>{status}</span>;
}

export function Empty({ children }) {
  return <div className="rounded-2xl border border-dashed border-[#E9E2D6] bg-white px-6 py-16 text-center font-body text-sm text-[#A79C8A]">{children}</div>;
}
